import {Spinner, TextInput} from '@volkswagen-onehub/components-core';
import * as React from 'react';
import Div from '../../components/Div.styled';
import EmptyResult from '../../components/EmptyResults';
import Icon from '../../components/Icon';
import SpaceLike from '../../components/Space';
import InputWrapperWithLabel from '../../components/StyledInputWrapperWithLabel';
import TypographyBody from '../../components/Typography/TypographyBody';
import {convertDataForQuestionManualTwo} from '../../helpers/questionManualTransformData';
import {
  QuestionManualContent,
  Result,
  ResultQuestionManualTwo,
  ResultQuestions,
  SelectManualProps,
} from '../../types/questionManuals';
import {config} from '../../environment';

type QuestionManualTwoProps = {
  handleNextStep: () => void;
  currentQuestion: QuestionManualContent;
  handleSelect: (data: SelectManualProps) => void;
  results: ResultQuestions[];
};

export interface TransformedData {
  key: number | string;
  option: string | string[];
  makecode: string | string[];
}

export default function QuestionManualTwo({
  handleNextStep,
  currentQuestion,
  handleSelect,
  results,
}: QuestionManualTwoProps) {
  const [value, setValue] = React.useState('');
  const [filteredData, setFilteredData] = React.useState<Result[]>([]);
  const [data, setData] = React.useState<Result[]>([]);
  const [loading, setLoading] = React.useState(true);
  const makeQuestion = React.useMemo(() => {
    return results.find((item) => item.id === 'make');
  }, [results]);
  React.useEffect(() => {
    const fetchState = async () => {
      try {
        if (makeQuestion && makeQuestion.makeCode) {
          const questionTwoData = await fetch(
            `${config.API_REDBOOK_MODELS_URL}`,
            {
              method: 'POST',
              body: JSON.stringify({
                makecode: makeQuestion.makeCode,
              }),
              headers: {
                'Content-Type': 'application/json',
              },
              mode: 'cors', // Change to 'cors' instead of 'no-cors' for cross-origin requests
            },
          );
          const result: {data: ResultQuestionManualTwo[]} =
            await questionTwoData.json();
          const resultData = convertDataForQuestionManualTwo(result.data);
          setData(resultData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchState();
  }, [makeQuestion]);
  React.useEffect(() => {
    if (data && data.length > 0) {
      const currentData = [...data];
      const lowercasedInput = value.toLowerCase();
      const filteredArray = currentData.map((item) => {
        const filteredOptions = item.option.filter((option) =>
          option.description.toLowerCase().includes(lowercasedInput),
        );
        return {
          key: item.key,
          option: filteredOptions.length > 0 ? filteredOptions : [],
        };
      });

      const finalFilteredArray = filteredArray.filter(
        (item) => item.option.length > 0,
      );
      setFilteredData(finalFilteredArray);
    }
  }, [data, value]);

  const handleInputChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const inputValue = target.value;
    setValue(inputValue);
  };

  return (
    <>
      <Div width="100%">
        <InputWrapperWithLabel>
          <TextInput
            appearance={'default'}
            type="text"
            placeholder="Start typing your make"
            value={value}
            onChange={(event) => handleInputChange(event)}
            label="Vehicle Make"
            hasClearButton
            isClearButtonVisible
            onClear={() => {
              setValue('');
              setFilteredData([...filteredData]);
            }}
            clearIconLabel="Clear search"
          />
        </InputWrapperWithLabel>
      </Div>
      <SpaceLike
        direction="vertical"
        size={40}
        customStyles={{
          marginTop: 40,
        }}
        className="no-scroll"
        width={'100%'}
      >
        {loading ? (
          <Div width="100%" customStyles={{textAlign: 'center'}}>
            <Spinner variant="large" />
          </Div>
        ) : filteredData?.length > 0 ? (
          filteredData.map((item, index) => {
            return (
              <Div key={index} customStyles={{color: 'white'}}>
                <TypographyBody style={{margin: '0 0 16px', fontSize: 20}}>
                  {item.key}
                </TypographyBody>
                <SpaceLike direction="vertical" size={16}>
                  {Array.isArray(item.option) &&
                    item.option?.map((a, i) => {
                      return (
                        <TypographyBody
                          key={i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            margin: 0,
                            fontSize: 16,
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            handleSelect({
                              value: a.description || '',
                              id: currentQuestion.id || '',
                              makeCode: a.makeCode || '',
                              vehicletype: a.vehicletype,
                              familyCode: a.familyCode,
                            });
                            handleNextStep();
                          }}
                        >
                          {a.description}
                          <Icon icon="arrow-right-alt" color="white" />
                        </TypographyBody>
                      );
                    })}
                </SpaceLike>
              </Div>
            );
          })
        ) : (
          <EmptyResult />
        )}
      </SpaceLike>
    </>
  );
}
